import React, { useEffect, useRef, useState } from "react";

/**
 * options: [{ value, label, hint?, group?, disabled? }]
 * `group` (e.g. city name) renders as a sticky section header in the dropdown
 * when at least one option has a group set.
 * `disabled` renders the option grayed out and non-selectable (e.g. already assigned).
 */
const SearchableMultiSelect = ({
  label,
  options = [],
  selected = [],
  onChange,
  placeholder = "Search & select...",
  disabled = false,
  error,
  required = false,
  emptyMessage = "No options available",
  onSearchChange,
  serverFiltered = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!onSearchChange) return;
    const timeoutId = setTimeout(() => {
      onSearchChange(query.trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [query, onSearchChange]);

  const filteredOptions = serverFiltered
    ? options
    : options.filter((opt) =>
      opt.label.toLowerCase().includes(query.trim().toLowerCase())
    );

  // Group in first-seen order. If nothing has a group, this collapses to one bucket.
  const groupedOptions = [];
  const groupIndex = new Map();
  filteredOptions.forEach((opt) => {
    const key = opt.group || "__ungrouped__";
    if (!groupIndex.has(key)) {
      groupIndex.set(key, groupedOptions.length);
      groupedOptions.push({ groupName: opt.group || null, items: [] });
    }
    groupedOptions[groupIndex.get(key)].items.push(opt);
  });

  groupedOptions.forEach((bucket) => {
    bucket.items.sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled));
  });

  const selectedOptions = options.filter((opt) => selected.includes(opt.value));

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const removeChip = (value, e) => {
    e.stopPropagation();
    onChange(selected.filter((v) => v !== value));
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div className="amf-field" ref={wrapperRef}>
      <label className="amf-label">
        {label} {required && <span className="amf-req">*</span>}
      </label>

      <div
        className={`amsel-control ${error ? "amsel-control--error" : ""} ${disabled ? "amsel-control--disabled" : ""
          } ${open ? "amsel-control--open" : ""}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        {selectedOptions.length === 0 ? (
          <span className="amsel-placeholder">{placeholder}</span>
        ) : (
          <div className="amsel-chips">
            {selectedOptions.map((opt) => (
              <span className="amsel-chip" key={opt.value}>
                {opt.label}
                {opt.group && <span className="amsel-chip-group">· {opt.group}</span>}
                <button
                  type="button"
                  className="amsel-chip-remove"
                  onClick={(e) => removeChip(opt.value, e)}
                  aria-label={`Remove ${opt.label}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="amsel-control-actions">
          {selectedOptions.length > 0 && !disabled && (
            <button
              type="button"
              className="amsel-clear-btn"
              onClick={clearAll}
              aria-label="Clear all"
            >
              Clear
            </button>
          )}
          <svg
            className={`amsel-chevron ${open ? "amsel-chevron--open" : ""}`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {open && !disabled && (
        <div className="amsel-dropdown">
          <div className="amsel-search-wrap">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              className="amsel-search-input"
              placeholder="Type to search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {query && (
              <button type="button" className="amsel-search-clear" onClick={() => setQuery("")}>
                ×
              </button>
            )}
          </div>

          <div className="amsel-options">
            {options.length === 0 ? (
              <div className="amsel-empty">{emptyMessage}</div>
            ) : filteredOptions.length === 0 ? (
              <div className="amsel-empty">No matches for "{query}"</div>
            ) : (
              groupedOptions.map((bucket, idx) => (
                <div className="amsel-group" key={bucket.groupName || `g-${idx}`}>
                  {bucket.groupName && (
                    <div className="amsel-group-label">{bucket.groupName}</div>
                  )}
                  {bucket.items.map((opt) => {
                    const isChecked = selected.includes(opt.value);
                    const isDisabled = !!opt.disabled;
                    return (
                      <div
                        key={opt.value}
                        className={`amsel-option ${isChecked ? "amsel-option--checked" : ""} ${isDisabled ? "amsel-option--disabled" : ""
                          }`}
                        onClick={() => !isDisabled && toggleOption(opt.value)}
                        aria-disabled={isDisabled}
                      >
                        <span className={`amsel-checkbox ${isChecked ? "amsel-checkbox--checked" : ""}`}>
                          {isChecked && (
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </span>
                        <span className="amsel-option-label">{opt.label}</span>
                        {isDisabled && <span className="amsel-option-hint amsel-option-hint--taken">Assigned</span>}
                        {!isDisabled && opt.hint && <span className="amsel-option-hint">{opt.hint}</span>}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {selectedOptions.length > 0 && (
            <div className="amsel-footer">{selectedOptions.length} selected</div>
          )}
        </div>
      )}

      {error && <span className="amf-error-msg">{error}</span>}
    </div>
  );
};

export default SearchableMultiSelect;